/* eslint-disable @typescript-eslint/no-floating-promises */
import axios from "axios";
import { type UserType } from "../redux/slices/manageUsers/manageUsersTypes";
import {
  type InstanceConfigType,
  type PaginationParams,
  type MessageType,
  type RequestLogsType,
} from "../redux/slices/midservice/midserviceTypes";
import { store } from "../redux/store";
import { userLogout } from "../redux/slices/user/authActions";
import { type GetSalesStatisticsParams } from "../redux/slices/statistics/statisticsTypes";
import { message } from "antd";

import axiosRetry from "axios-retry";
import { type PacketConfigurationRequestType } from "../redux/slices/PacketConfiguration/PacketConfigurationTypes";
import { getApiUrl } from "../utils/url";
// import { type PacketConfigurationType } from "../redux/slices/PacketConfiguration/PacketConfigurationTypes";

const baseUrl = `${getApiUrl()}/api/v1`;
const base = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  headers: {
    accepts: "application/json",
  },
});

const urlsSkipAuth = ["/user/login", "user/logout"];
const dangerousMethods = ["post", "put", "patch", "delete"];

function getCookie(name: string): string {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const res = parts.pop()?.split(";").shift();
    return res ?? "default";
  }
  return "default";
}

base.interceptors.request.use(async (config) => {
  if (config.url != null && urlsSkipAuth.includes(config.url)) {
    return config;
  }
  console.log("axios conf", config);
  if (
    config?.method != null &&
    config?.xsrfHeaderName != null &&
    config?.xsrfCookieName != null
  ) {
    if (dangerousMethods.includes(config.method)) {
      config.headers = {
        ...config.headers,
        "X-CSRF-Token": getCookie("csrf_access_token"),
      };
    }
  }

  console.log("state", store.getState().user);
  return config;
});

axiosRetry(base, {
  retries: 3, // number of retries
  retryDelay: (retryCount: number) => {
    console.log(`retry attempt: ${retryCount}`);
    return retryCount * 2000; // time interval between retries
  },
  retryCondition: async (error: any) => {
    // message.error(error.message);
    // if (error.code === "ERR_NETWORK") {
    //   message.error("unexpected error");
    // }
    if (error.code === "ERR_CANCELED") {
      message.warning(`Повторение запроса...`);
      return true;
    }
    return false;
  },
});

base.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    console.log("error", error);
    const originalRequest = error.response.config;
    console.log("originalRequest._retry", originalRequest);
    const urlTokens = error?.request?.responseURL.split("/");
    const url = urlTokens[urlTokens.length - 1];
    console.log("url", url);
    if (
      error.response.status === 401 &&
      !(originalRequest?._retry != null) &&
      url !== "login" &&
      url !== "refresh" &&
      url !== "logout"
    ) {
      originalRequest._retry = true;
      const res = await API.refreshToken().catch(
        async () => await store.dispatch(userLogout()),
      );
      console.log("res", res);
      return await base(originalRequest);
    }
    return await Promise.reject(error);
  },
);

export const API = {
  async login(username: string, password: string, remember: boolean) {
    console.log("document.cookie");
    const bodyFormData = new FormData();
    bodyFormData.append("username", username);
    bodyFormData.append("password", password);
    if (remember) bodyFormData.append("remember", "1");
    const request = await base.post(`/user/login`, bodyFormData);

    console.log("document.cookie", request.headers);

    return request;
  },

  async logout() {
    const request = await base.delete(`/user/logout`);
    return request;
  },
  async refreshToken() {
    const request = await base.get(`/user/refresh`);
    return request;
  },

  async getUser() {
    console.log("api getUser");
    return await base.get(`/user/me`);
  },

  async getMidserviceInstancesList(instancesListParams: PaginationParams) {
    let query: string = `/midservice-instance?page=${instancesListParams.page}&limit=${instancesListParams.limit}`;
    if (instancesListParams.filter != null) {
      query += `&filter=${instancesListParams.filter}`;
    }
    if (instancesListParams.search != null) {
      query += `&search=${instancesListParams.search}`;
    }

    return await base.get(query);
  },

  async getIssuesList(issuesListParams: PaginationParams) {
    let query: string = `/issue/?page=${issuesListParams.page}&limit=${issuesListParams.limit}`;
    if (issuesListParams.search != null) {
      query += `&search=${issuesListParams.search}`;
    }
    return await base.get(query);
  },

  async getSingleIssue(issueId: number) {
    return await base.get(`/issue/${issueId}`);
  },

  async getParticipantsList() {
    return await base.get(`/config/users-list`);
  },

  async createIssueComment(issueId: number, comment: string, files: File[]) {
    const formData = new FormData();

    formData.append("request", JSON.stringify({ text: comment }));

    files.forEach((file) => {
      formData.append("files", file);
    });

    return await base.post(`/issue/${issueId}/create-comment`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  async getIssueCommentFile(
    issueId: number,
    filename: string,
    onProgress: (progress: number) => void,
  ) {
    return await base.get(
      `/issue/${issueId}/comments/files?filename=${filename}`,
      {
        responseType: "blob",
        onDownloadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total ?? 1),
          );
          onProgress(percent);
        },
      },
    );
  },

  async setIssueStatus(issueId: number, status: string) {
    console.log("api setIssueStatus");

    return await base.post("/issue/set-status", { issue_id: issueId, status });
  },

  async getSingleInstance(midserviceId: number) {
    console.log("api getSingleMidservice");
    return await base.get(`/midservice-instance/${midserviceId}`);
  },
  async getMidserviceEnumOptions() {
    console.log("api getMidserviceEnumOptions");
    return await base.get(`/config/midserviceEnumOptions`);
  },

  async getModulesParams(modules: any) {
    console.log("api getModulesParams");

    return await base.post(`/config/modules-params`, modules);
  },

  async getEventsIds() {
    console.log("api getEventsIds");

    return await base.get(`/config/event-ids`);
  },

  // commands to instances

  async postConfig(
    domain: string,
    deviceId: string,
    config: InstanceConfigType,
  ) {
    console.log("api postConfig");
    return await base.post(
      `/server-commands/set-config/${deviceId}?domain=${domain}`,
      config,
    );
  },
  async restartInstance(domain: string, deviceId: string) {
    console.log("api restartInstance");
    return await base.get(
      `server-commands/restart/${deviceId}?domain=${domain}`,
    );
  },
  async deleteLogs(domain: string, deviceId: string) {
    console.log("api restartInstance");
    return await base.get(
      `server-commands/reset-logs/${deviceId}?domain=${domain}`,
    );
  },
  async requestLogs(getLogsRequestParams: RequestLogsType) {
    console.log("api requestLogs");
    return await base.post(
      `/server-commands/get-logs/${getLogsRequestParams.deviceId}?domain=${getLogsRequestParams.domain}`,
      getLogsRequestParams,
    );
  },
  async checkLogs(deviceId: string) {
    return await base.get(`/apilogs/device/${deviceId}`);
  },
  async switchApiLogs(deviceId: string, loggingDuration: number | null = null) {
    return await base.post(
      `/apilogs/${deviceId}${
        loggingDuration !== null ? `?ttl=${loggingDuration}` : ""
      }`,
    );
  },

  async sendCommand(domain: string, deviceId: string, params: MessageType) {
    console.log("api sendCommand");
    return await base.post(
      `/server-commands/cmd/${deviceId}?domain=${domain}`,
      params,
    );
  },

  async sendCommandsToDeviceList(
    message: MessageType,
    devices?: Record<string, Record<string, string>>,
    search?: string,
  ) {
    return await base.post(
      `/server-commands/cmd${search !== undefined ? `?search=${search}` : ""}`,
      {
        devices,
        message,
      },
    );
  },

  async getInstanceMethods() {
    return await base.get(`/server-commands/methods`);
  },

  async deleteInstance(instanceId: number) {
    console.log("api deleteInstance");
    return await base.delete(`/midservice-instance/${instanceId}`);
  },

  // manage users api
  async getUsersList(paginationParams: PaginationParams) {
    let query: string = `/users?page=${paginationParams.page}&limit=${paginationParams.limit}`;
    if (paginationParams.filter != null) {
      query += `&filter=${paginationParams.filter}`;
    }
    if (paginationParams.search != null) {
      query += `&search=${paginationParams.search}`;
    }

    return await base.get(query);
  },

  async createUser(params: UserType) {
    console.log("api sendCommand", params);
    return await base.post(`/users`, params);
  },

  async updateUser(params: UserType) {
    console.log("api sendCommand", params);
    return await base.put(`/users/${params.id}`, params);
  },

  async getAvailableZones() {
    console.log("api getAvailableZones");
    return await base.get(`/users/available-zones`);
  },

  async getAvailableRoles() {
    console.log("api getAvailableZones");
    return await base.get(`/users/available-roles`);
  },

  async deleteUser(userId: number) {
    console.log("api sendCommand", userId);
    return await base.delete(`/users/${userId}`);
  },

  // statistics api
  async getSalesStatistics(params: GetSalesStatisticsParams) {
    console.log("api getSalesStatistics", params);
    return await base.post(`/statistics/${params.domain}/${params.addressId}`, {
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    });
  },

  async getTTInfo(domain: string, addressId: number) {
    console.log("api getTTInfo", domain, addressId);
    return await base.get(`/statistics/${domain}/${addressId}`);
  },

  // PacketConfigurationApi
  async getConfigurationsList(paginationParams: PaginationParams) {
    let query: string = `/packet-configuration?page=${paginationParams.page}&limit=${paginationParams.limit}`;
    if (paginationParams.filter != null) {
      query += `&filter=${paginationParams.filter}`;
    }
    if (paginationParams.search != null) {
      query += `&search=${paginationParams.search}`;
    }

    return await base.get(query);
  },

  async createPacketConfiguration(params: PacketConfigurationRequestType) {
    console.log("api createPacketConfiguration", params);
    return await base.post(`/packet-configuration`, params);
  },

  async updatePacketConfiguration(params: PacketConfigurationRequestType) {
    console.log("api createPacketConfiguration", params);
    return await base.put(
      `/packet-configuration/${params?.id ?? "-1"}`,
      params,
    );
  },

  async retrievePacketConfiguration(packetConfigurationId: number) {
    console.log("api retrievePacketConfiguration", packetConfigurationId);
    return await base.get(`/packet-configuration/${packetConfigurationId}`);
  },

  async deletePacketConfiguration(packetConfigurationId: number) {
    console.log("api deletePacketConfiguration", packetConfigurationId);
    return await base.delete(`/packet-configuration/${packetConfigurationId}`);
  },
  async deployPacketConfiguration(packetConfigurationId: number) {
    console.log("api retrievePacketConfiguration", packetConfigurationId);
    return await base.post(
      `/packet-configuration/deploy/${packetConfigurationId}`,
    );
  },

  async downloadInstancesFile() {
    console.log("api downloadInstancesFile");
    return await base.get(`/reports/devices_csv`);
  },

  async getExchangeFiles() {
    return await base.get("/exchange/files");
  },

  async uploadExchangeFiles(
    files: FormData,
    onProgress: (progress: number) => void,
  ) {
    return await base.post(`/exchange/upload-files`, files, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total ?? 1),
        );
        onProgress(percent);
      },
    });
  },

  async downloadExchangeFile(
    filename: string,
    onProgress: (progress: number) => void,
  ) {
    return await base.get(`/exchange/download-file?filename=${filename}`, {
      responseType: "blob",
      onDownloadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total ?? 1),
        );
        onProgress(percent);
      },
    });
  },

  async deleteExchangeFiles(filenames: string[]) {
    return await base.delete("/exchange/delete-files", {
      data: filenames,
    });
  },
};
